import React, { useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import {
  Users,
  UserCheck,
  UserX,
  Flag,
  UserPlus,
  Activity,
  GraduationCap,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSummary,
  fetchActiveUsersInfo,
  fetchfetpLevelStats,
  fetchAvailabityStats
} from '../redux/SummarySlice';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const OverviewPage = () => {
  const dispatch = useDispatch();

  const {
    summary: { data: summaryData, isLoading: summaryLoading },
    activeUsers: { data: activeUsersData, isLoading: activeUsersLoading },
    fetpLevelStats: { data: fetpData, isLoading: fetpLoading },
    availabilityStats: { data: availabilityData, isLoading: availabilityLoading }
  } = useSelector((state) => state.summary);

  useEffect(() => {
    dispatch(fetchSummary());
    dispatch(fetchActiveUsersInfo());
    dispatch(fetchfetpLevelStats());
    dispatch(fetchAvailabityStats());
  }, [dispatch]);

  const activeUsersChartData = {
    labels: activeUsersData?.map(item => item.month) || [],
    datasets: [{
      label: 'New Users',
      data: activeUsersData?.map(item => item.active_users) || [],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.1
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      }
    }
  };

  // Mentorship Pie Chart Data
  const mentorshipChartData = {
    labels: ['Mentees', 'Mentors'],
    datasets: [
      {
        data: [availabilityData?.mentee || 0, availabilityData?.mentor || 0],
        backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)'],
        borderColor: ['rgb(59, 130, 246)', 'rgb(16, 185, 129)'],
        borderWidth: 1,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20,
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((sum, value) => sum + value, 0);
            const value = context.raw;
            const percentage = Math.round((value / total) * 100);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
  };

  const LoadingSkeleton = ({ className }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
  );

  const StatCard = ({ icon: Icon, label, value, subValue, subLabel, color, loading }) => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center space-x-4">
        <div className={`p-2 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          {loading ? (
            <LoadingSkeleton className="h-8 w-20 mt-1" />
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-800">{value}</p>
              {subValue !== undefined && (
                <div className="flex items-center mt-2 text-sm">
                  <span className="text-gray-600">{subLabel}:</span>
                  <span className="ml-1 font-medium text-gray-900">{subValue}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800">Analytics Dashboard</h1>

      {/* Users Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={summaryData?.total_users || 0}
          subLabel="Inactive"
          subValue={summaryData?.inactive_users || 0}
          color="bg-blue-500"
          loading={summaryLoading}
        />
        <StatCard
          icon={Activity}
          label="Active Users"
          value={summaryData?.active_users || 0}
          subLabel="Active Rate"
          subValue={`${Math.round((summaryData?.active_users / summaryData?.total_users || 0) * 100)}%`}
          color="bg-green-500"
          loading={summaryLoading}
        />
        <StatCard
          icon={UserPlus}
          label="Pending Applications"
          value={summaryData?.applicant_pending || 0}
          subLabel="Approved"
          subValue={summaryData?.applicant_approved || 0}
          color="bg-yellow-500"
          loading={summaryLoading}
        />
        <StatCard
          icon={UserX}
          label="Rejected Applications"
          value={summaryData?.applicant_rejected || 0}
          color="bg-red-500"
          loading={summaryLoading}
        />
      </div>

      {/* Reports Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Flag}
          label="Total Reports"
          value={summaryData?.reported_posts || 0}
          subLabel="Pending"
          subValue={summaryData?.pending_reports || 0}
          color="bg-purple-500"
          loading={summaryLoading}
        />
        <StatCard
          icon={CheckCircle}
          label="Retained Reports"
          value={summaryData?.retained_reports || 0}
          color="bg-emerald-500"
          loading={summaryLoading}
        />
        <StatCard
          icon={XCircle}
          label="Removed Reports"
          value={summaryData?.removed_reports || 0}
          color="bg-rose-500"
          loading={summaryLoading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Users Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 mb-6">
            <Users className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-800">New Users Trend</h2>
          </div>
          {activeUsersLoading ? (
            <LoadingSkeleton className="h-[300px] w-full" />
          ) : (
            <div className="h-[300px]">
              <Line data={activeUsersChartData} options={chartOptions} />
            </div>
          )}
        </div>

        {/* FETP Levels */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 mb-6">
            <GraduationCap className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-800">FETP Level Distribution</h2>
          </div>
          {fetpLoading ? (
            <LoadingSkeleton className="h-[300px] w-full" />
          ) : (
            <div className="space-y-6">
              {['frontline', 'intermediate', 'advanced'].map((level, index) => (
                <div key={level} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-500' :
                          index === 1 ? 'bg-yellow-500' : 'bg-green-500'
                        }`} />
                      <span className="text-sm font-medium text-gray-700 capitalize">{level}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {fetpData?.[level] || 0}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${index === 0 ? 'bg-blue-500' :
                          index === 1 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                      style={{
                        width: `${((fetpData?.[level] || 0) / (summaryData?.total_users || 100)) * 100}%`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Mentorship Stats */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header with Response Deployment */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-800">Mentorship Availability</h2>
            </div>
            <div className="bg-blue-50 rounded-lg px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Response Deployment: {`(${availabilityData?.response_deployment||''})`} </span>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-2">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full" 
                      style={{ width: `${availabilityData?.response_deployment || 0}%` }}
                    ></div>
                  </div>
                  <span className="text-lg font-bold text-blue-600">{availabilityData?.response_deployment || 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content */}
        {availabilityLoading ? (
          <LoadingSkeleton className="h-[200px] w-full" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Stats Cards */}
            <div className="md:col-span-1">
              <div className="space-y-4">
                {/* Mentees Card */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <h3 className="text-base font-semibold text-blue-700 mb-2">Mentees</h3>
                  <div className="flex items-center">
                    <span className="text-2xl font-bold text-blue-600">{availabilityData?.mentee || 0}</span>
                    <span className="ml-2 text-xs text-blue-500">Total</span>
                  </div>
                </div>
                
                {/* Mentors Card */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <h3 className="text-base font-semibold text-green-700 mb-2">Mentors</h3>
                  <div className="flex items-center">
                    <span className="text-2xl font-bold text-green-600">{availabilityData?.mentor || 0}</span>
                    <span className="ml-2 text-xs text-green-500">Total</span>
                  </div>
                </div>
                
                {/* Ratio Card */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <h3 className="text-base font-semibold text-purple-700 mb-2">Mentee:Mentor Ratio</h3>
                  <div className="flex items-center">
                    <span className="text-2xl font-bold text-purple-600">
                      {availabilityData ? (availabilityData.mentee / availabilityData.mentor).toFixed(1) : 0}:1
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Chart */}
            <div className="md:col-span-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-base font-semibold text-gray-700 mb-4">Distribution</h3>
              <div className="h-52">
                <Pie data={mentorshipChartData} options={pieChartOptions} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewPage;